// Audio detection module for live guitar note detection
// Uses Web Audio API and autocorrelation for pitch detection

import AudioDeviceDetection from './audioDeviceDetection.js';

class AudioDetection {
    constructor(onNoteDetected, onWaveformUpdate, onVolumeUpdate, onDeviceChange, onFrequencyUpdate, onFFTUpdate, onLog) {
        this.onNoteDetected = onNoteDetected;
        this.onWaveformUpdate = onWaveformUpdate;
        this.onVolumeUpdate = onVolumeUpdate;
        this.onDeviceChange = onDeviceChange;
        this.onFrequencyUpdate = onFrequencyUpdate;
        this.onFFTUpdate = onFFTUpdate;
        this.onLog = onLog;
        this.audioContext = null;
        this.analyser = null;
        this.microphone = null;
        this.mediaStream = null;
        this.audioDeviceLabel = null;
        this.currentDeviceId = null;
        this.isListening = false;
        this.animationFrame = null;
        
        // Initialize audio device detection module
        this.deviceDetection = new AudioDeviceDetection(
            (message, ...args) => this.log(message, ...args),
            (message, ...args) => this.logWarn(message, ...args),
            (message, ...args) => this.logError(message, ...args)
        );
        
        // Audio analysis parameters
        this.bufferSize = 8192;
        this.sampleRate = 44100;
        
        // Decimation parameters for improved frequency resolution
        // Decimation factor of 4 gives ~11kHz sampling rate (Nyquist ~5.5kHz) at 44.1kHz
        this.decimationFactor = 4; // Downsample by 4x
        this.decimatedSampleRate = null; // Will be set based on actual sample rate
        
        // Visualization parameters
        this.fftVisualizationBins = 128; // Number of bins for FFT visualization
        this.waveformVisualizationPoints = 128; // Number of points for waveform visualization
        
        // Note detection threshold
        this.minConfidence = 0.85;
        this.minVolume = 0.01;
        
        // Polyphonic detection parameters
        this.maxSimultaneousNotes = 6; // Maximum number of notes to detect at once (typical for guitar chords)
        this.minPeakHeight = 80; // Minimum FFT magnitude for a peak (0-255 scale)
        this.minPeakDistance = 8; // Minimum distance between peaks in frequency bins
        
        // Temporal smoothing parameters
        this.minFramesForStability = 5; // Number of consecutive frames a note must appear (≈83ms at 60 FPS)
        this.maxFrameAge = 10; // Maximum number of frames to keep in history
        this.noteHistory = []; // Array of frame detections: [{ timestamp, notes: [...] }, ...]
        this.lastReportedNotes = []; // Last set of notes reported to avoid duplicate callbacks
        
        // Detection mode and configuration
        this.detectionMode = 'poly'; // 'mono' or 'poly'
        this.enablePreFiltering = true; // Toggle band-pass filtering
        this.enableHPS = true; // Toggle Harmonic Product Spectrum
        this.enableSubharmonicCheck = true; // Toggle subharmonic checking
        this.enableAdaptiveThreshold = true; // Toggle adaptive noise floor
        this.enableFifthValidation = false; // Toggle fifth-based note validation (default off)
        this.enableStrictHarmonics = false; // Toggle strict harmonic validation (default off)
        this.requiredHarmonicsCount = 2; // Number of additional harmonics beyond root (1-8, default 2 checks root+2x+3x)
        
        // Noise floor tracking for adaptive thresholding
        this.noiseFloorHistory = [];
        this.noiseFloorWindowSize = 30;
        this.noiseFloor = 0;
        
        // Hysteresis for note stability
        this.noteStates = {}; // { 'E4': { state: 'on'|'off', onFrames: 0, offFrames: 0 } }
        this.hysteresisOnThreshold = 3;  // frames to turn on
        this.hysteresisOffThreshold = 6; // frames to turn off (higher = more sticky)
        
        // Mono mode temporal smoothing
        this.monoFrequencyHistory = []; // For median filtering in mono mode
        this.monoHistorySize = 5;
        
        // Debug logging flag (can be enabled for troubleshooting)
        this.debug = false;
        
        // Standard tuning notes (E2, A2, D3, G3, B3, E4) frequencies
        this.noteFrequencies = this.generateNoteFrequencies();
    }
    
    // Helper method to format log messages with arguments
    formatLogMessage(message, args) {
        return args.length > 0 
            ? `${message} ${args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' ')}`
            : message;
    }
    
    // Helper method to log messages both to console and UI
    log(message, ...args) {
        // Log to console
        console.log(message, ...args);
        
        // Log to UI if callback is provided
        if (this.onLog) {
            this.onLog(this.formatLogMessage(message, args));
        }
    }
    
    // Helper method to log warnings
    logWarn(message, ...args) {
        console.warn(message, ...args);
        if (this.onLog) {
            this.onLog(`⚠️ ${this.formatLogMessage(message, args)}`);
        }
    }
    
    // Helper method to log errors
    logError(message, ...args) {
        console.error(message, ...args);
        if (this.onLog) {
            this.onLog(`❌ ERROR: ${this.formatLogMessage(message, args)}`);
        }
    }
    
    // Set detection mode (mono or poly)
    setDetectionMode(mode) {
        if (mode !== 'mono' && mode !== 'poly') {
            console.warn(`Invalid detection mode: ${mode}. Must be 'mono' or 'poly'.`);
            return;
        }
        
        this.detectionMode = mode;
        
        // Clear history when switching modes
        this.noteHistory = [];
        this.lastReportedNotes = [];
        this.monoFrequencyHistory = [];
        this.noteStates = {};
        
        if (this.debug) {
            console.log(`Detection mode changed to: ${mode}`);
        }
    }
    
    // Generate all note frequencies from C0 to B8
    generateNoteFrequencies() {
        const noteNames = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
        const notes = [];
        
        // Generate notes from C0 to B8
        for (let octave = 0; octave <= 8; octave++) {
            for (let noteIndex = 0; noteIndex < 12; noteIndex++) {
                const noteName = noteNames[noteIndex];
                const noteNumber = (octave * 12) + noteIndex;
                // A4 = 440 Hz, note number 57 (octave 4, note index 9)
                const frequency = 440 * Math.pow(2, (noteNumber - 57) / 12);
                
                // Only include frequencies that guitars can produce (E2 to ~E6)
                if (frequency >= 80 && frequency <= 1320) {
                    notes.push({
                        name: noteName,
                        octave: octave,
                        frequency: frequency,
                        fullName: noteName + octave
                    });
                }
            }
        }
        
        return notes;
    }
    
    
    // Delegate device detection methods to deviceDetection module
    isBuiltInDevice(label) {
        return this.deviceDetection.isBuiltInDevice(label);
    }
    
    isUsbOrExternalDevice(label) {
        return this.deviceDetection.isUsbOrExternalDevice(label);
    }
    
    async selectPreferredAudioDevice() {
        return await this.deviceDetection.selectPreferredAudioDevice();
    }

    async getAvailableDevices() {
        return await this.deviceDetection.getAvailableDevices();
    }
    
    // Switch to a different audio device
    async switchDevice(deviceId) {
        const wasListening = this.isListening;
        
        if (wasListening) {
            // Stop current stream but preserve the audio context
            if (this.animationFrame) {
                cancelAnimationFrame(this.animationFrame);
                this.animationFrame = null;
            }
            
            if (this.microphone) {
                this.microphone.disconnect();
                this.microphone = null;
            }
            
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => track.stop());
                this.mediaStream = null;
            }
        }
        
        // Start with new device
        if (wasListening) {
            await this.startWithDevice(deviceId);
        } else {
            // Just update the device ID for next start
            this.currentDeviceId = deviceId;
        }
    }

    // Start listening with a specific device
    async startWithDevice(deviceId) {
        try {
            const baseAudioConstraints = {
                echoCancellation: false,
                autoGainControl: true,
                noiseSuppression: false,
                latency: 0
            };
            
            const audioConstraints = { ...baseAudioConstraints };
            if (deviceId) {
                audioConstraints.deviceId = { exact: deviceId };
                this.log('Starting audio with specific device ID:', this.deviceDetection.truncateDeviceId(deviceId));
            } else {
                this.log('Starting audio with browser default device');
            }
            
            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: audioConstraints
            });
            
            this.mediaStream = stream;
            
            // Get the audio device label
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length > 0) {
                this.audioDeviceLabel = audioTracks[0].label || 'Default Audio Device';
                const trackSettings = audioTracks[0].getSettings();
                
                this.log('✓ Successfully started audio stream:');
                this.log(`  Device: ${this.audioDeviceLabel}`);
                this.log(`  Sample Rate: ${trackSettings.sampleRate || 'unknown'} Hz`);
                this.log(`  Channel Count: ${trackSettings.channelCount || 'unknown'}`);
                this.log(`  Device ID: ${this.deviceDetection.truncateDeviceId(trackSettings.deviceId)}`);
                
                if (this.onDeviceChange) {
                    this.onDeviceChange(this.audioDeviceLabel);
                }
            }
            
            // Reuse existing audio context or create new one
            if (!this.audioContext || this.audioContext.state === 'closed') {
                this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
                this.sampleRate = this.audioContext.sampleRate;
            }
            
            // Set decimated sample rate based on actual sample rate
            this.decimatedSampleRate = this.sampleRate / this.decimationFactor;
            
            // Create new analyser
            this.analyser = this.audioContext.createAnalyser();
            this.analyser.fftSize = 16384;
            this.analyser.smoothingTimeConstant = 0.4; // Reduced from 0.7 for better transient response
            
            // Create audio filters for pre-processing if enabled
            if (this.enablePreFiltering) {
                // High-pass filter at 80 Hz (removes rumble and handling noise, above lowest guitar note)
                this.highpassFilter = this.audioContext.createBiquadFilter();
                this.highpassFilter.type = 'highpass';
                this.highpassFilter.frequency.value = 80;
                this.highpassFilter.Q.value = 0.7071; // Butterworth response
                
                // Low-pass filter at 6000 Hz (removes hiss, keeps useful harmonics)
                this.lowpassFilter = this.audioContext.createBiquadFilter();
                this.lowpassFilter.type = 'lowpass';
                this.lowpassFilter.frequency.value = 6000;
                this.lowpassFilter.Q.value = 0.7071;
                
                // Notch filter at 50 Hz (removes mains hum - EU)
                this.notchFilter50 = this.audioContext.createBiquadFilter();
                this.notchFilter50.type = 'notch';
                this.notchFilter50.frequency.value = 50;
                this.notchFilter50.Q.value = 10; // Narrow notch
                
                // Notch filter at 60 Hz (removes mains hum - US) - narrow enough not to interfere with highpass
                this.notchFilter60 = this.audioContext.createBiquadFilter();
                this.notchFilter60.type = 'notch';
                this.notchFilter60.frequency.value = 60;
                this.notchFilter60.Q.value = 30; // Very narrow notch to avoid interaction with highpass at 80Hz
            }
            
            // Log FFT resolution if debug is enabled
            if (this.debug) {
                const binResolution = this.decimatedSampleRate / this.analyser.fftSize;
                console.log(`FFT Configuration:`);
                console.log(`  FFT Size: ${this.analyser.fftSize}`);
                console.log(`  Frequency Bin Count: ${this.analyser.frequencyBinCount}`);
                console.log(`  Original Sample Rate: ${this.sampleRate} Hz`);
                console.log(`  Decimation Factor: ${this.decimationFactor}x`);
                console.log(`  Decimated Sample Rate: ${this.decimatedSampleRate.toFixed(2)} Hz`);
                console.log(`  Bin Resolution: ${binResolution.toFixed(3)} Hz/bin`);
                console.log(`  Nyquist Frequency: ${(this.decimatedSampleRate / 2).toFixed(2)} Hz`);
                console.log(`  Smoothing: ${this.analyser.smoothingTimeConstant}`);
                console.log(`  Pre-filtering: ${this.enablePreFiltering ? 'enabled' : 'disabled'}`);
            }
            
            // Connect microphone through filter chain to analyser
            this.microphone = this.audioContext.createMediaStreamSource(stream);
            
            if (this.enablePreFiltering) {
                // Connect filters: microphone → highpass → lowpass → notch50 → notch60 → analyser
                this.microphone.connect(this.highpassFilter);
                this.highpassFilter.connect(this.lowpassFilter);
                this.lowpassFilter.connect(this.notchFilter50);
                this.notchFilter50.connect(this.notchFilter60);
                this.notchFilter60.connect(this.analyser);
            } else {
                // Direct connection without filtering
                this.microphone.connect(this.analyser);
            }
            
            this.isListening = true;
            
            // Only set currentDeviceId after successful initialization
            this.currentDeviceId = deviceId;
            
            // Start detection loop
            this.detectPitch();
            
            return true;
        } catch (error) {
            this.logError('Error starting with device:', error);
            throw error;
        }
    }

    // Start listening to microphone
    async start(deviceId = null) {
        if (this.isListening) {
            return;
        }
        
        try {
            // If a specific device ID is provided, use it
            if (deviceId) {
                return await this.startWithDevice(deviceId);
            }
            
            // Select preferred audio device (this will request permission internally)
            const preferredDeviceId = await this.selectPreferredAudioDevice();
            
            // Start with the preferred device (or browser default)
            return await this.startWithDevice(preferredDeviceId);
            
        } catch (error) {
            this.logError('Error accessing microphone:', error);
            throw error;
        }
    }
    
    // Stop listening to microphone
    stop() {
        // Clear note history and last reported notes regardless of listening state
        this.noteHistory = [];
        this.lastReportedNotes = [];
        this.monoFrequencyHistory = [];
        this.noteStates = {};
        this.noiseFloorHistory = [];
        
        if (!this.isListening) {
            return;
        }
        
        this.isListening = false;
        
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        
        if (this.microphone) {
            this.microphone.disconnect();
            this.microphone = null;
        }
        
        // Disconnect filters if they exist
        if (this.highpassFilter) {
            this.highpassFilter.disconnect();
            this.highpassFilter = null;
        }
        if (this.lowpassFilter) {
            this.lowpassFilter.disconnect();
            this.lowpassFilter = null;
        }
        if (this.notchFilter50) {
            this.notchFilter50.disconnect();
            this.notchFilter50 = null;
        }
        if (this.notchFilter60) {
            this.notchFilter60.disconnect();
            this.notchFilter60 = null;
        }
        
        // Stop all media stream tracks
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => track.stop());
            this.mediaStream = null;
        }
        
        if (this.audioContext && this.audioContext.state !== 'closed') {
            this.audioContext.close();
            this.audioContext = null;
        }
        
        this.analyser = null;
        this.audioDeviceLabel = null;
        // Preserve currentDeviceId so user selection persists when toggling Live mode
    }
    
    // Apply Hann window to reduce spectral leakage
    applyHannWindow(buffer) {
        const N = buffer.length;
        const windowedBuffer = new Float32Array(N);
        
        for (let n = 0; n < N; n++) {
            // Hann window formula: w[n] = 0.5 * (1 - cos(2 * π * n / (N - 1)))
            // where n is the sample index (0 to N-1) and N is the window length
            const windowValue = 0.5 * (1 - Math.cos(2 * Math.PI * n / (N - 1)));
            windowedBuffer[n] = buffer[n] * windowValue;
        }
        
        return windowedBuffer;
    }
    
    // Apply anti-aliasing lowpass filter before decimation
    // Uses a simple moving average filter to prevent aliasing
    applyAntiAliasingFilter(buffer, cutoffRatio = 0.4) {
        // Simple moving average filter as anti-aliasing
        // Cutoff at 40% of new Nyquist (0.4 * 2756.25 = ~1.1025 kHz fundamental + harmonics)
        const filterLength = Math.ceil(1.0 / cutoffRatio);
        const filtered = new Float32Array(buffer.length);
        
        for (let i = 0; i < buffer.length; i++) {
            let sum = 0;
            let count = 0;
            const start = Math.max(0, i - Math.floor(filterLength / 2));
            const end = Math.min(buffer.length, i + Math.ceil(filterLength / 2));
            
            for (let j = start; j < end; j++) {
                sum += buffer[j];
                count++;
            }
            filtered[i] = sum / count;
        }
        
        return filtered;
    }
    
    // Decimate signal by taking every Nth sample after anti-aliasing
    // This improves frequency resolution for guitar note detection
    decimateSignal(buffer, factor) {
        if (factor === 1) {
            return buffer; // No decimation needed
        }
        
        // Apply anti-aliasing filter first
        const filtered = this.applyAntiAliasingFilter(buffer, 0.4);
        
        // Downsample by taking every Nth sample
        const decimatedLength = Math.floor(buffer.length / factor);
        const decimated = new Float32Array(decimatedLength);
        
        for (let i = 0; i < decimatedLength; i++) {
            decimated[i] = filtered[i * factor];
        }
        
        return decimated;
    }
    
    // Autocorrelation function for pitch detection
    autoCorrelate(buffer, sampleRate) {
        // Apply decimation for better frequency resolution in guitar range
        const decimatedBuffer = this.decimateSignal(buffer, this.decimationFactor);
        const effectiveSampleRate = this.decimatedSampleRate;
        
        // Check if buffer has sufficient volume
        let rms = 0;
        for (let i = 0; i < decimatedBuffer.length; i++) {
            rms += decimatedBuffer[i] * decimatedBuffer[i];
        }
        rms = Math.sqrt(rms / decimatedBuffer.length);
        
        if (rms < this.minVolume) {
            return -1; // Not enough signal
        }
        
        // Implements the autocorrelation algorithm for pitch detection
        // Based on the algorithm from https://github.com/cwilso/PitchDetect
        
        const bufferLength = decimatedBuffer.length;
        const correlations = new Float32Array(bufferLength);
        
        // Calculate autocorrelation
        for (let lag = 0; lag < bufferLength; lag++) {
            let sum = 0;
            for (let i = 0; i < bufferLength - lag; i++) {
                sum += decimatedBuffer[i] * decimatedBuffer[i + lag];
            }
            correlations[lag] = sum;
        }
        
        // Find the first zero crossing after the initial peak
        let firstZeroCrossing = 0;
        for (let i = 1; i < bufferLength; i++) {
            if (correlations[i] > 0 && correlations[i - 1] <= 0) {
                firstZeroCrossing = i;
                break;
            }
        }
        
        // Search for the highest correlation peak after the first zero crossing
        // This represents the fundamental period
        let maxCorrelation = correlations[firstZeroCrossing];
        let maxCorrelationIndex = firstZeroCrossing;
        
        for (let i = firstZeroCrossing + 1; i < bufferLength / 2; i++) {
            if (correlations[i] > maxCorrelation) {
                maxCorrelation = correlations[i];
                maxCorrelationIndex = i;
            }
        }
        
        // If we didn't find a strong correlation, return -1
        if (maxCorrelation <= 0 || maxCorrelationIndex === 0) {
            return -1;
        }
        
        // Use parabolic interpolation for sub-sample accuracy
        let refinedIndex = maxCorrelationIndex;
        if (maxCorrelationIndex > 0 && maxCorrelationIndex < correlations.length - 1) {
            const s0 = correlations[maxCorrelationIndex - 1];
            const s1 = correlations[maxCorrelationIndex];
            const s2 = correlations[maxCorrelationIndex + 1];
            
            // Parabolic interpolation formula
            const denominator = 2 * s1 - s0 - s2;
            if (denominator !== 0) {
                const adjustment = 0.5 * (s0 - s2) / denominator;
                refinedIndex = maxCorrelationIndex + adjustment;
            }
        }
        
        // Convert the period (in samples) to frequency using decimated sample rate
        return effectiveSampleRate / refinedIndex;
    }
    
    // Find the closest note to a given frequency
    findClosestNote(frequency) {
        if (!frequency || frequency <= 0) {
            return null;
        }
        
        let closestNote = null;
        let minDifference = Infinity;
        
        for (const note of this.noteFrequencies) {
            const difference = Math.abs(note.frequency - frequency);
            if (difference < minDifference) {
                minDifference = difference;
                closestNote = note;
            }
        }
        
        // Check if the detected frequency is close enough to the note
        if (closestNote) {
            const centsDifference = 1200 * Math.log2(frequency / closestNote.frequency);
            
            // Allow up to 50 cents deviation (half a semitone)
            if (Math.abs(centsDifference) < 50) {
                return {
                    ...closestNote,
                    detectedFrequency: frequency,
                    cents: centsDifference,
                    confidence: 1 - (Math.abs(centsDifference) / 50)
                };
            }
        }
        
        return null;
    }
    
    // Harmonic Product Spectrum - emphasizes fundamentals over harmonics
    harmonicProductSpectrum(frequencyData, numHarmonics = 8) {
        const length = frequencyData.length;
        const hpsSpectrum = new Float32Array(length);
        
        // Initialize with the original spectrum
        for (let i = 0; i < length; i++) {
            hpsSpectrum[i] = frequencyData[i];
        }
        
        // Multiply by downsampled versions
        for (let harmonic = 2; harmonic <= numHarmonics; harmonic++) {
            for (let i = 0; i < Math.floor(length / harmonic); i++) {
                const downsampledIndex = i * harmonic;
                if (downsampledIndex < length) {
                    hpsSpectrum[i] *= frequencyData[downsampledIndex];
                }
            }
        }
        
        // Normalize the result to prevent overflow
        let maxValue = 0;
        for (let i = 0; i < length; i++) {
            if (hpsSpectrum[i] > maxValue) {
                maxValue = hpsSpectrum[i];
            }
        }
        
        if (maxValue > 0) {
            for (let i = 0; i < length; i++) {
                hpsSpectrum[i] = (hpsSpectrum[i] / maxValue) * 255;
            }
        }
        
        return hpsSpectrum;
    }
    
    // Check if a frequency is likely a harmonic of another frequency
    isHarmonic(frequency, fundamentalFrequency, tolerance = 0.05) {
        // Check if frequency is a multiple of the fundamental (2x, 3x, 4x, 5x, 6x, 7x, 8x)
        for (let harmonic = 2; harmonic <= 8; harmonic++) {
            const expectedHarmonic = fundamentalFrequency * harmonic;
            const ratio = frequency / expectedHarmonic;
            // Allow some tolerance for imperfect tuning
            if (Math.abs(ratio - 1) < tolerance) {
                return true;
            }
        }
        return false;
    }
    
    // Check if a fundamental frequency has its required harmonics present in the spectrum
    // This validates that a note is truly being played by checking for harmonics
    hasRequiredHarmonics(fundamentalFreq, frequencyData, tolerance = 0.05) {
        // When strict harmonics is disabled, use the original behavior (root + 2x + 3x)
        if (!this.enableStrictHarmonics) {
            // Calculate expected harmonic frequencies
            const firstHarmonic = fundamentalFreq * 2;  // 1st harmonic (2x fundamental)
            const secondHarmonic = fundamentalFreq * 3; // 2nd harmonic (3x fundamental)
            
            // Get effective sample rate and FFT size for bin calculations
            const effectiveSampleRate = this.decimatedSampleRate;
            const fftSize = this.analyser.fftSize;
            const binResolution = effectiveSampleRate / fftSize;
            const nyquistFreq = effectiveSampleRate / 2;
            
            // Calculate bins for each harmonic
            const fundamentalBin = Math.round(fundamentalFreq / binResolution);
            
            // Define search range in bins based on tolerance
            const searchRange = Math.ceil((fundamentalFreq * tolerance) / binResolution);
            
            // Check if all three frequencies are present with sufficient magnitude
            // We need to find peaks within the tolerance range for each harmonic
            
            // Check fundamental (root)
            let foundRoot = false;
            let rootMagnitude = 0;
            for (let i = Math.max(0, fundamentalBin - searchRange); 
                 i <= Math.min(frequencyData.length - 1, fundamentalBin + searchRange); 
                 i++) {
                if (frequencyData[i] > this.minPeakHeight) {
                    foundRoot = true;
                    rootMagnitude = Math.max(rootMagnitude, frequencyData[i]);
                }
            }
            
            if (!foundRoot) {
                if (this.debug) {
                    console.log(`  Harmonic check failed: Root frequency ${fundamentalFreq.toFixed(2)} Hz not found`);
                }
                return false;
            }
            
            // Check 1st harmonic (2x)
            let firstHarmonicSearchFreq = firstHarmonic;
            let firstHarmonicAliased = false;
            
            // Check if it exceeds Nyquist and calculate aliased frequency if needed
            if (firstHarmonic > nyquistFreq) {
                firstHarmonicSearchFreq = 2 * nyquistFreq - firstHarmonic;
                firstHarmonicAliased = true;
                
                // If aliased frequency is out of range, we can't verify it
                if (firstHarmonicSearchFreq < 0 || firstHarmonicSearchFreq > nyquistFreq) {
                    if (this.debug) {
                        console.log(`  Harmonic check warning: 1st harmonic ${firstHarmonic.toFixed(2)} Hz exceeds Nyquist, ` +
                                   `aliased to ${firstHarmonicSearchFreq.toFixed(2)} Hz (out of range)`);
                    }
                    // If harmonic is beyond Nyquist and can't be aliased, we can't verify it, but don't fail the check
                    return true;
                }
                
                if (this.debug) {
                    console.log(`  Checking aliased 1st harmonic: ${firstHarmonic.toFixed(2)} Hz -> ${firstHarmonicSearchFreq.toFixed(2)} Hz`);
                }
            }
            
            const firstHarmonicBin = Math.round(firstHarmonicSearchFreq / binResolution);
            let foundFirstHarmonic = false;
            let firstHarmonicMagnitude = 0;
            for (let i = Math.max(0, firstHarmonicBin - searchRange); 
                 i <= Math.min(frequencyData.length - 1, firstHarmonicBin + searchRange); 
                 i++) {
                if (frequencyData[i] > this.minPeakHeight * 0.5) { // Allow lower threshold for harmonics
                    foundFirstHarmonic = true;
                    firstHarmonicMagnitude = Math.max(firstHarmonicMagnitude, frequencyData[i]);
                }
            }
            
            if (!foundFirstHarmonic) {
                if (this.debug) {
                    const freqInfo = firstHarmonicAliased ? 
                        `${firstHarmonic.toFixed(2)} Hz (aliased at ${firstHarmonicSearchFreq.toFixed(2)} Hz)` :
                        `${firstHarmonic.toFixed(2)} Hz`;
                    console.log(`  Harmonic check failed: 1st harmonic ${freqInfo} not found for root ${fundamentalFreq.toFixed(2)} Hz`);
                }
                return false;
            }
            
            // Check 2nd harmonic (3x)
            let secondHarmonicSearchFreq = secondHarmonic;
            let secondHarmonicAliased = false;
            
            // Check if it exceeds Nyquist and calculate aliased frequency if needed
            if (secondHarmonic > nyquistFreq) {
                secondHarmonicSearchFreq = 2 * nyquistFreq - secondHarmonic;
                secondHarmonicAliased = true;
                
                // If aliased frequency is out of range, we can't verify it
                if (secondHarmonicSearchFreq < 0 || secondHarmonicSearchFreq > nyquistFreq) {
                    if (this.debug) {
                        console.log(`  Harmonic check warning: 2nd harmonic ${secondHarmonic.toFixed(2)} Hz exceeds Nyquist, ` +
                                   `aliased to ${secondHarmonicSearchFreq.toFixed(2)} Hz (out of range)`);
                    }
                    // We found root and 1st harmonic, which is good enough if 2nd is beyond detectable range
                    return true;
                }
                
                if (this.debug) {
                    console.log(`  Checking aliased 2nd harmonic: ${secondHarmonic.toFixed(2)} Hz -> ${secondHarmonicSearchFreq.toFixed(2)} Hz`);
                }
            }
            
            const secondHarmonicBin = Math.round(secondHarmonicSearchFreq / binResolution);
            let foundSecondHarmonic = false;
            let secondHarmonicMagnitude = 0;
            for (let i = Math.max(0, secondHarmonicBin - searchRange); 
                 i <= Math.min(frequencyData.length - 1, secondHarmonicBin + searchRange); 
                 i++) {
                if (frequencyData[i] > this.minPeakHeight * 0.3) { // Even lower threshold for 2nd harmonic
                    foundSecondHarmonic = true;
                    secondHarmonicMagnitude = Math.max(secondHarmonicMagnitude, frequencyData[i]);
                }
            }
            
            if (!foundSecondHarmonic) {
                if (this.debug) {
                    const freqInfo = secondHarmonicAliased ? 
                        `${secondHarmonic.toFixed(2)} Hz (aliased at ${secondHarmonicSearchFreq.toFixed(2)} Hz)` :
                        `${secondHarmonic.toFixed(2)} Hz`;
                    console.log(`  Harmonic check failed: 2nd harmonic ${freqInfo} not found for root ${fundamentalFreq.toFixed(2)} Hz`);
                }
                return false;
            }
            
            // All three harmonics found!
            if (this.debug) {
                console.log(`  ✓ Harmonic check passed for ${fundamentalFreq.toFixed(2)} Hz: ` +
                           `root=${rootMagnitude.toFixed(1)}, ` +
                           `1st=${firstHarmonicMagnitude.toFixed(1)}${firstHarmonicAliased ? ' (aliased)' : ''}, ` +
                           `2nd=${secondHarmonicMagnitude.toFixed(1)}${secondHarmonicAliased ? ' (aliased)' : ''}`);
            }
            
            return true;
        }
        
        // Strict harmonics mode - check for configurable number of harmonics
        const effectiveSampleRate = this.decimatedSampleRate;
        const fftSize = this.analyser.fftSize;
        const binResolution = effectiveSampleRate / fftSize;
        const searchRange = Math.ceil((fundamentalFreq * tolerance) / binResolution);
        const nyquistFreq = effectiveSampleRate / 2;
        
        // Magnitude thresholds for each harmonic level
        // Higher harmonics typically have lower magnitude
        const thresholds = [
            1.0,   // Fundamental (1x)
            0.5,   // First harmonic (2x fundamental) - 50%
            0.3,   // Second harmonic (3x fundamental) - 30%
            0.2,   // Third harmonic (4x fundamental) - 20%
            0.15,  // Fourth harmonic (5x fundamental) - 15%
            0.15,  // Fifth harmonic (6x fundamental) - 15%
            0.12,  // Sixth harmonic (7x fundamental) - 12%
            0.12,  // Seventh harmonic (8x fundamental) - 12%
            0.10,  // Eighth harmonic (9x fundamental) - 10%
            0.10,  // Ninth harmonic (10x fundamental) - 10%
            0.08   // Tenth harmonic (11x fundamental) - 8%
        ];
        
        const foundHarmonics = [];
        const missingHarmonics = [];
        
        // Check fundamental and each additional harmonic up to requiredHarmonicsCount
        // Loop iterates from harmonicIndex 0 to requiredHarmonicsCount (inclusive)
        // This checks requiredHarmonicsCount + 1 total harmonics including the fundamental
        // Examples: count=1 → 2 total (fundamental+2x), count=2 → 3 total (fundamental+2x+3x)
        for (let harmonicIndex = 0; harmonicIndex <= this.requiredHarmonicsCount; harmonicIndex++) {
            const harmonicMultiplier = harmonicIndex + 1; // 1x, 2x, 3x, 4x, 5x, 6x, 7x, 8x, 9x
            const harmonicFreq = fundamentalFreq * harmonicMultiplier;
            const threshold = this.minPeakHeight * thresholds[harmonicIndex];
            
            let searchFreq = harmonicFreq;
            let isAliased = false;
            
            // Check if this harmonic exceeds Nyquist frequency
            if (harmonicFreq > nyquistFreq) {
                // Calculate the aliased (reflected) frequency
                // When a signal exceeds Nyquist, it aliases back into the spectrum
                // The aliased frequency is: 2*Nyquist - harmonicFreq (for first-order aliasing)
                // This handles the case where harmonics "fold back" due to sampling limitations
                searchFreq = 2 * nyquistFreq - harmonicFreq;
                isAliased = true;
                
                // If the aliased frequency is negative or still above Nyquist, 
                // we can't reliably detect it, so skip this harmonic
                if (searchFreq < 0 || searchFreq > nyquistFreq) {
                    if (this.debug) {
                        console.log(`  Harmonic check: ${harmonicIndex === 0 ? 'Root' : `${harmonicMultiplier}x harmonic`} ` +
                                   `${harmonicFreq.toFixed(2)} Hz exceeds Nyquist, aliased to ${searchFreq.toFixed(2)} Hz (out of range), skipping`);
                    }
                    // Don't fail validation for harmonics that can't be detected
                    continue;
                }
                
                if (this.debug) {
                    console.log(`  Harmonic check: ${harmonicIndex === 0 ? 'Root' : `${harmonicMultiplier}x harmonic`} ` +
                               `${harmonicFreq.toFixed(2)} Hz exceeds Nyquist (${nyquistFreq.toFixed(2)} Hz), ` +
                               `checking aliased frequency at ${searchFreq.toFixed(2)} Hz`);
                }
            }
            
            const searchBin = Math.round(searchFreq / binResolution);
            
            // Search for peak in the expected frequency range
            let found = false;
            let magnitude = 0;
            for (let i = Math.max(0, searchBin - searchRange); 
                 i <= Math.min(frequencyData.length - 1, searchBin + searchRange); 
                 i++) {
                if (frequencyData[i] > threshold) {
                    found = true;
                    magnitude = Math.max(magnitude, frequencyData[i]);
                }
            }
            
            const harmonicName = harmonicIndex === 0 ? 'root' : `${harmonicMultiplier}x`;
            if (found) {
                foundHarmonics.push({ 
                    name: harmonicName, 
                    freq: harmonicFreq, 
                    searchFreq: searchFreq,
                    aliased: isAliased,
                    magnitude 
                });
            } else {
                missingHarmonics.push({ name: harmonicName, freq: harmonicFreq });
                
                // For strict mode, we require all harmonics (that are within Nyquist or can be detected via aliasing)
                if (this.debug) {
                    const freqInfo = isAliased ? 
                        `${harmonicFreq.toFixed(2)} Hz (aliased at ${searchFreq.toFixed(2)} Hz)` :
                        `${harmonicFreq.toFixed(2)} Hz`;
                    console.log(`  Harmonic check failed: ${harmonicName} harmonic ${freqInfo} not found ` +
                               `for root ${fundamentalFreq.toFixed(2)} Hz (threshold: ${threshold.toFixed(1)})`);
                }
                return false;
            }
        }
        
        // All required harmonics found!
        if (this.debug) {
            const harmonicSummary = foundHarmonics.map(h => 
                `${h.name}=${h.magnitude.toFixed(1)}`
            ).join(', ');
            console.log(`  ✓ Strict harmonic check passed for ${fundamentalFreq.toFixed(2)} Hz: ${harmonicSummary}`);
        }
        
        return true;
    }
    
    // Check if two notes have a perfect fifth relationship
    // A perfect fifth is 7 semitones (frequency ratio of approximately 1.5 or 3:2)
    isPerfectFifth(freq1, freq2, tolerance = 0.03) {
        // Get the frequency ratio (higher / lower)
        const higher = Math.max(freq1, freq2);
        const lower = Math.min(freq1, freq2);
        const ratio = higher / lower;
        
        // Check for perfect fifth in any octave
        // Perfect fifth: 1.5 (3/2), 3.0 (3/2 * 2), 0.75 (3/4 - inverted fifth/perfect fourth)
        const fifthRatios = [
            1.5,        // Perfect fifth
            3.0,        // Perfect fifth + octave
            0.75,       // Inverted fifth (perfect fourth)
        ];
        
        for (const targetRatio of fifthRatios) {
            if (Math.abs(ratio - targetRatio) / targetRatio < tolerance) {
                return true;
            }
        }
        
        return false;
    }
    
    // Validate detected notes using perfect fifth relationships
    // If we detect a note and its fifth, boost confidence in both
    validateWithFifths(notes) {
        if (!this.enableFifthValidation || notes.length < 2) {
            return notes;
        }
        
        // Create a map to track which notes have fifth relationships
        const fifthPairs = new Set();
        
        // Check all pairs of notes for fifth relationships
        for (let i = 0; i < notes.length; i++) {
            for (let j = i + 1; j < notes.length; j++) {
                if (this.isPerfectFifth(notes[i].frequency, notes[j].frequency)) {
                    fifthPairs.add(i);
                    fifthPairs.add(j);
                    
                    if (this.debug) {
                        console.log(`  Fifth relationship detected: ${notes[i].fullName} and ${notes[j].fullName}`);
                    }
                }
            }
        }
        
        // If fifth validation is enabled and we found fifth pairs,
        // we can boost confidence or filter out notes without pairs
        // For now, we'll just mark which notes have fifth support
        const validatedNotes = notes.map((note, index) => {
            return {
                ...note,
                hasFifthSupport: fifthPairs.has(index)
            };
        });
        
        if (this.debug && fifthPairs.size > 0) {
            console.log(`Fifth validation: ${fifthPairs.size} notes with fifth support out of ${notes.length}`);
        }
        
        return validatedNotes;
    }
    
    // Filter out harmonic peaks, keeping only fundamental frequencies
    filterHarmonics(peaks) {
        if (peaks.length <= 1) {
            return peaks;
        }
        
        // Sort peaks by magnitude (strongest first) for initial processing
        // This works well for guitar where fundamentals are typically stronger
        const sortedPeaks = [...peaks].sort((a, b) => b.magnitude - a.magnitude);
        
        const fundamentals = [];
        
        for (const peak of sortedPeaks) {
            let isHarmonicOfExisting = false;
            
            // Check if this peak is a harmonic of any already identified fundamental
            for (const fundamental of fundamentals) {
                if (this.isHarmonic(peak.frequency, fundamental.frequency)) {
                    isHarmonicOfExisting = true;
                    if (this.debug) {
                        console.log(`  Filtered harmonic: ${peak.frequency.toFixed(2)} Hz (harmonic of ${fundamental.frequency.toFixed(2)} Hz)`);
                    }
                    break;
                }
            }
            
            // Also check if any existing fundamental is actually a harmonic of this peak
            // This handles the rare case where a harmonic is stronger than the fundamental
            if (!isHarmonicOfExisting && peak.frequency < 500) { // Only check for lower frequencies
                for (let i = fundamentals.length - 1; i >= 0; i--) {
                    if (this.isHarmonic(fundamentals[i].frequency, peak.frequency)) {
                        // Remove the harmonic from fundamentals
                        if (this.debug) {
                            console.log(`  Replaced harmonic ${fundamentals[i].frequency.toFixed(2)} Hz with fundamental ${peak.frequency.toFixed(2)} Hz`);
                        }
                        fundamentals.splice(i, 1);
                    }
                }
            }
            
            // Subharmonic checking - prevent octave-up errors
            if (!isHarmonicOfExisting && this.enableSubharmonicCheck) {
                const subharmonic = peak.frequency / 2;
                
                // Check if there's a peak at f/2 with better harmonic pattern
                if (subharmonic >= 80) { // Within guitar range
                    for (const otherPeak of sortedPeaks) {
                        const freqRatio = Math.abs(otherPeak.frequency - subharmonic) / subharmonic;
                        
                        // If we found a peak near f/2 with reasonable magnitude
                        if (freqRatio < 0.05 && otherPeak.magnitude > peak.magnitude * 0.5) {
                            // Use HPS to determine which is more likely the fundamental
                            if (this.enableHPS && peak.hpsScore !== undefined && otherPeak.hpsScore !== undefined) {
                                if (otherPeak.hpsScore > peak.hpsScore * 1.2) {
                                    // The subharmonic has a better HPS score, skip this peak
                                    isHarmonicOfExisting = true;
                                    if (this.debug) {
                                        console.log(`  Filtered octave error: ${peak.frequency.toFixed(2)} Hz (subharmonic ${subharmonic.toFixed(2)} Hz has better HPS score)`);
                                    }
                                    break;
                                }
                            }
                        }
                    }
                }
            }
            
            // If not a harmonic, add it as a potential fundamental
            if (!isHarmonicOfExisting) {
                fundamentals.push(peak);
            }
        }
        
        if (this.debug && fundamentals.length < peaks.length) {
            console.log(`Harmonic filtering: ${peaks.length} peaks -> ${fundamentals.length} fundamentals`);
        }
        
        return fundamentals;
    }
    
    // Update adaptive noise floor based on minimum FFT bin values
    updateNoiseFloor(frequencyData) {
        if (!this.enableAdaptiveThreshold) {
            return;
        }
        
        // Find minimum value in frequency data (excluding DC and very low frequencies)
        let minValue = 255;
        const startBin = 5; // Skip first few bins to avoid DC component
        
        for (let i = startBin; i < frequencyData.length; i++) {
            if (frequencyData[i] < minValue) {
                minValue = frequencyData[i];
            }
        }
        
        // Add to history
        this.noiseFloorHistory.push(minValue);
        
        // Keep history within window size
        if (this.noiseFloorHistory.length > this.noiseFloorWindowSize) {
            this.noiseFloorHistory.shift();
        }
        
        // Calculate rolling average
        let sum = 0;
        for (let i = 0; i < this.noiseFloorHistory.length; i++) {
            sum += this.noiseFloorHistory[i];
        }
        this.noiseFloor = sum / this.noiseFloorHistory.length;
        
        // Update minimum peak height (15 dB above noise floor)
        this.minPeakHeight = this.noiseFloor + 15;
        
        if (this.debug && this.noiseFloorHistory.length === this.noiseFloorWindowSize) {
            console.log(`Noise floor: ${this.noiseFloor.toFixed(2)}, Min peak height: ${this.minPeakHeight.toFixed(2)}`);
        }
    }
    
    // Detect multiple notes using FFT (Polyphonic detection)
    detectMultipleFrequencies(buffer, sampleRate) {
        // Apply decimation for better frequency resolution in guitar range
        const decimatedBuffer = this.decimateSignal(buffer, this.decimationFactor);
        const effectiveSampleRate = this.decimatedSampleRate;
        
        // Check if buffer has sufficient volume
        let rms = 0;
        for (let i = 0; i < decimatedBuffer.length; i++) {
            rms += decimatedBuffer[i] * decimatedBuffer[i];
        }
        rms = Math.sqrt(rms / decimatedBuffer.length);
        
        if (rms < this.minVolume) {
            return []; // Not enough signal
        }
        
        // Get frequency domain data using FFT
        const frequencyData = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteFrequencyData(frequencyData);
        
        // Update adaptive noise floor
        this.updateNoiseFloor(frequencyData);
        
        // Apply HPS if enabled for better fundamental detection
        let processedData = frequencyData;
        if (this.enableHPS) {
            processedData = this.harmonicProductSpectrum(frequencyData);
        }
        
        // Find peaks in the frequency spectrum
        const peaks = [];
        
        // Define guitar frequency range (E2 at 82.41 Hz to E6 at 1318.51 Hz)
        const minGuitarFreq = 80;
        const maxGuitarFreq = 1320;
        
        // Scan through frequency bins to find local maxima
        for (let i = 2; i < frequencyData.length - 2; i++) {
            const current = frequencyData[i];
            
            // Check if this is a local maximum and above threshold
            if (current > this.minPeakHeight &&
                current > frequencyData[i - 1] &&
                current > frequencyData[i - 2] &&
                current > frequencyData[i + 1] &&
                current > frequencyData[i + 2]) {
                
                // Use parabolic interpolation for sub-bin frequency accuracy
                const s0 = frequencyData[i - 1];
                const s1 = frequencyData[i];
                const s2 = frequencyData[i + 1];
                
                // Parabolic interpolation formula with bounds checking
                let refinedBin = i;
                const denominator = 2 * s1 - s0 - s2;
                if (denominator !== 0) {
                    const adjustment = 0.5 * (s0 - s2) / denominator;
                    // Clamp adjustment to reasonable bounds (-0.5 to 0.5)
                    const clampedAdjustment = Math.max(-0.5, Math.min(0.5, adjustment));
                    refinedBin = i + clampedAdjustment;
                }
                
                // Convert bin to frequency using decimated sample rate
                const frequency = (refinedBin * effectiveSampleRate) / this.analyser.fftSize;
                
                // Only consider frequencies within guitar range
                if (frequency < minGuitarFreq || frequency > maxGuitarFreq) {
                    continue;
                }
                
                // Get HPS score if available
                let hpsScore = current;
                if (this.enableHPS && i < processedData.length) {
                    hpsScore = processedData[i];
                }
                
                // Check distance from previous peaks (use integer bin for consistency)
                let tooClose = false;
                let peakIndexToReplace = -1;
                
                for (let j = 0; j < peaks.length; j++) {
                    const peak = peaks[j];
                    // Use integer bin indices for distance comparison
                    if (Math.abs(peak.intBin - i) < this.minPeakDistance) {
                        // If this peak is stronger, mark the previous one for replacement
                        if (current > peak.magnitude) {
                            peakIndexToReplace = j;
                        } else {
                            tooClose = true;
                        }
                        break;
                    }
                }
                
                if (peakIndexToReplace >= 0) {
                    // Replace the weaker peak
                    peaks[peakIndexToReplace] = {
                        intBin: i,           // Integer bin for distance calculations
                        bin: refinedBin,     // Refined bin for accurate frequency
                        frequency: frequency,
                        magnitude: current,
                        hpsScore: hpsScore   // HPS score for subharmonic checking
                    };
                } else if (!tooClose) {
                    // Add new peak
                    peaks.push({
                        intBin: i,           // Integer bin for distance calculations
                        bin: refinedBin,     // Refined bin for accurate frequency
                        frequency: frequency,
                        magnitude: current,
                        hpsScore: hpsScore   // HPS score for subharmonic checking
                    });
                }
            }
        }
        
        if (this.debug && peaks.length > 0) {
            console.log(`Detected ${peaks.length} peaks before harmonic filtering:`);
            peaks.forEach(p => console.log(`  ${p.frequency.toFixed(2)} Hz (magnitude: ${p.magnitude}, HPS: ${p.hpsScore.toFixed(2)})`));
        }
        
        // Filter out harmonics to get fundamental frequencies
        const fundamentalPeaks = this.filterHarmonics(peaks);
        
        // Validate each fundamental by checking for required harmonics (root, 1st, 2nd)
        // This ensures we only detect notes that have a complete harmonic structure
        const validatedPeaks = fundamentalPeaks.filter(peak => {
            const hasHarmonics = this.hasRequiredHarmonics(peak.frequency, frequencyData);
            if (!hasHarmonics && this.debug) {
                console.log(`  Rejected ${peak.frequency.toFixed(2)} Hz - missing required harmonics`);
            }
            return hasHarmonics;
        });
        
        if (this.debug && validatedPeaks.length > 0) {
            console.log(`After harmonic validation: ${validatedPeaks.length} notes confirmed`);
        }
        
        // Sort by magnitude (strongest first)
        validatedPeaks.sort((a, b) => b.magnitude - a.magnitude);
        
        // Take top peaks (limit to avoid too many false positives)
        const topPeaks = validatedPeaks.slice(0, this.maxSimultaneousNotes);
        
        // Convert frequencies to notes
        const detectedFrequencies = topPeaks.map(peak => peak.frequency);
        
        if (this.debug && detectedFrequencies.length > 0) {
            console.log(`Final detected frequencies: ${detectedFrequencies.map(f => f.toFixed(2)).join(', ')} Hz`);
        }
        
        return detectedFrequencies.filter(f => f > 0);
    }
    
    // Add current frame's detections to history
    addToHistory(notes) {
        const frame = {
            timestamp: Date.now(),
            notes: notes.map(note => ({
                fullName: note.fullName,
                name: note.name,
                octave: note.octave,
                frequency: note.frequency,
                confidence: note.confidence
            }))
        };
        
        this.noteHistory.push(frame);
        
        // Clean up old frames
        if (this.noteHistory.length > this.maxFrameAge) {
            this.noteHistory.shift();
        }
    }
    
    // Analyze history to find stable notes with hysteresis
    findStableNotes() {
        if (this.noteHistory.length < this.minFramesForStability) {
            // Not enough history yet
            return [];
        }
        
        // Get the last N frames for stability check
        const recentFrames = this.noteHistory.slice(-this.minFramesForStability);
        
        // Get all unique notes that appeared in recent frames
        const allNoteNames = new Set();
        for (const frame of recentFrames) {
            for (const note of frame.notes) {
                allNoteNames.add(note.fullName);
            }
        }
        
        // Also check previously reported notes that might still be on
        for (const note of this.lastReportedNotes) {
            allNoteNames.add(note.fullName);
        }
        
        // Initialize note states for new notes
        for (const noteName of allNoteNames) {
            if (!this.noteStates[noteName]) {
                this.noteStates[noteName] = {
                    state: 'off',
                    onFrames: 0,
                    offFrames: 0,
                    noteData: null
                };
            }
        }
        
        // Update frame counts for each note
        for (const noteName of allNoteNames) {
            const noteState = this.noteStates[noteName];
            
            // Check if note appears in the most recent frame
            const mostRecentFrame = recentFrames[recentFrames.length - 1];
            const noteInCurrentFrame = mostRecentFrame.notes.find(n => n.fullName === noteName);
            
            if (noteInCurrentFrame) {
                // Note detected in current frame
                noteState.onFrames++;
                noteState.offFrames = 0;
                noteState.noteData = noteInCurrentFrame;
                
                // Turn on if threshold reached
                if (noteState.state === 'off' && noteState.onFrames >= this.hysteresisOnThreshold) {
                    noteState.state = 'on';
                    if (this.debug) {
                        console.log(`Note ${noteName} turned ON (${noteState.onFrames} consecutive frames)`);
                    }
                }
            } else {
                // Note not detected in current frame
                noteState.onFrames = 0;
                noteState.offFrames++;
                
                // Turn off if threshold reached
                if (noteState.state === 'on' && noteState.offFrames >= this.hysteresisOffThreshold) {
                    noteState.state = 'off';
                    if (this.debug) {
                        console.log(`Note ${noteName} turned OFF (${noteState.offFrames} frames absent)`);
                    }
                }
            }
        }
        
        // Collect all notes that are currently in 'on' state
        const stableNotes = [];
        for (const noteState of Object.values(this.noteStates)) {
            if (noteState.state === 'on' && noteState.noteData) {
                stableNotes.push(noteState.noteData);
            }
        }
        
        // Clean up old note states that have been off for a while
        for (const noteName in this.noteStates) {
            const noteState = this.noteStates[noteName];
            if (noteState.state === 'off' && noteState.offFrames > this.hysteresisOffThreshold * 2) {
                delete this.noteStates[noteName];
            }
        }
        
        // Sort by frequency for consistent musical ordering (low to high)
        stableNotes.sort((a, b) => a.frequency - b.frequency);
        
        return stableNotes;
    }
    
    // Check if two note arrays are the same
    notesAreEqual(notes1, notes2) {
        if (notes1.length !== notes2.length) {
            return false;
        }
        
        // Sort both arrays by fullName for comparison
        const sorted1 = [...notes1].sort((a, b) => a.fullName.localeCompare(b.fullName));
        const sorted2 = [...notes2].sort((a, b) => a.fullName.localeCompare(b.fullName));
        
        for (let i = 0; i < sorted1.length; i++) {
            if (sorted1[i].fullName !== sorted2[i].fullName) {
                return false;
            }
        }
        
        return true;
    }
    
    // Main pitch detection loop
    detectPitch() {
        if (!this.isListening) {
            return;
        }
        
        const buffer = new Float32Array(this.analyser.fftSize);
        this.analyser.getFloatTimeDomainData(buffer);
        
        // Calculate RMS volume for the volume meter
        let rms = 0;
        for (let i = 0; i < buffer.length; i++) {
            rms += buffer[i] * buffer[i];
        }
        rms = Math.sqrt(rms / buffer.length);
        
        // Send volume data for visualization
        if (this.onVolumeUpdate) {
            this.onVolumeUpdate(rms);
        }
        
        // Send waveform data for visualization
        if (this.onWaveformUpdate) {
            const waveformData = [];
            const step = buffer.length / this.waveformVisualizationPoints;
            for (let i = 0; i < this.waveformVisualizationPoints; i++) {
                waveformData.push(buffer[Math.floor(i * step)]);
            }
            this.onWaveformUpdate(waveformData);
        }
        
        // Send FFT data for frequency spectrum visualization
        if (this.onFFTUpdate) {
            const fftData = new Uint8Array(this.analyser.frequencyBinCount);
            this.analyser.getByteFrequencyData(fftData);
            // Downsample FFT data for visualization
            const downsampledFFT = [];
            const fftStep = fftData.length / this.fftVisualizationBins;
            for (let i = 0; i < this.fftVisualizationBins; i++) {
                downsampledFFT.push(fftData[Math.floor(i * fftStep)]);
            }
            this.onFFTUpdate(downsampledFFT);
        }
        
        // Branch based on detection mode
        if (this.detectionMode === 'mono') {
            // Mono mode: Use autocorrelation for single note detection
            const frequency = this.autoCorrelate(buffer, this.sampleRate);
            
            if (frequency > 0) {
                // Add to mono frequency history for median filtering
                this.monoFrequencyHistory.push(frequency);
                if (this.monoFrequencyHistory.length > this.monoHistorySize) {
                    this.monoFrequencyHistory.shift();
                }
                
                // Apply median filter to smooth frequency
                let smoothedFrequency = frequency;
                if (this.monoFrequencyHistory.length >= 3) {
                    const sorted = [...this.monoFrequencyHistory].sort((a, b) => a - b);
                    const midIndex = Math.floor(sorted.length / 2);
                    smoothedFrequency = sorted[midIndex];
                }
                
                const note = this.findClosestNote(smoothedFrequency);
                
                if (note && note.confidence >= this.minConfidence) {
                    // Send frequency update
                    if (this.onFrequencyUpdate) {
                        this.onFrequencyUpdate([{
                            frequency: note.detectedFrequency,
                            note: note,
                            confidence: note.confidence
                        }]);
                    }
                    
                    // Add to history and find stable notes
                    this.addToHistory([note]);
                    const stableNotes = this.findStableNotes();
                    
                    // Only call the callback if stable notes have changed
                    if (this.onNoteDetected && !this.notesAreEqual(stableNotes, this.lastReportedNotes)) {
                        this.lastReportedNotes = stableNotes.map(note => ({ ...note }));
                        this.onNoteDetected(stableNotes);
                    }
                } else {
                    // No valid note detected
                    if (this.onFrequencyUpdate) {
                        this.onFrequencyUpdate(null);
                    }
                    
                    this.addToHistory([]);
                    const stableNotes = this.findStableNotes();
                    
                    if (this.onNoteDetected && !this.notesAreEqual(stableNotes, this.lastReportedNotes)) {
                        this.lastReportedNotes = stableNotes.map(note => ({ ...note }));
                        this.onNoteDetected(stableNotes);
                    }
                }
            } else {
                // No frequency detected
                if (this.onFrequencyUpdate) {
                    this.onFrequencyUpdate(null);
                }
                
                this.addToHistory([]);
                const stableNotes = this.findStableNotes();
                
                if (this.onNoteDetected && !this.notesAreEqual(stableNotes, this.lastReportedNotes)) {
                    this.lastReportedNotes = stableNotes.map(note => ({ ...note }));
                    this.onNoteDetected(stableNotes);
                }
            }
        } else {
            // Poly mode: Use FFT for multiple note detection
            const frequencies = this.detectMultipleFrequencies(buffer, this.sampleRate);
            
            // Convert frequencies to notes
            const detectedNotes = [];
            const allFrequencyData = [];
            
            for (const frequency of frequencies) {
                const note = this.findClosestNote(frequency);
                if (note) {
                    allFrequencyData.push({
                        frequency: note.detectedFrequency,
                        note: note,
                        confidence: note.confidence
                    });
                    
                    if (note.confidence >= this.minConfidence) {
                        detectedNotes.push(note);
                    }
                }
            }
            
            // Apply fifth validation if enabled
            const validatedNotes = this.validateWithFifths(detectedNotes);
            
            // Send frequency update with all detected frequencies
            if (this.onFrequencyUpdate) {
                if (allFrequencyData.length > 0) {
                    this.onFrequencyUpdate(allFrequencyData);
                } else {
                    this.onFrequencyUpdate(null);
                }
            }
            
            // Add current detections to history (use validated notes)
            this.addToHistory(validatedNotes);
            
            // Find stable notes from history
            const stableNotes = this.findStableNotes();
            
            // Only call the callback if stable notes have changed
            if (this.onNoteDetected) {
                if (!this.notesAreEqual(stableNotes, this.lastReportedNotes)) {
                    // Clone stable notes to prevent external modifications
                    this.lastReportedNotes = stableNotes.map(note => ({ ...note }));
                    this.onNoteDetected(stableNotes);
                }
            }
        }
        
        // Continue detection loop
        this.animationFrame = requestAnimationFrame(() => this.detectPitch());
    }
    
    // Check if audio detection is supported
    static isSupported() {
        return !!(navigator.mediaDevices && 
                  navigator.mediaDevices.getUserMedia && 
                  (window.AudioContext || window.webkitAudioContext));
    }
    
    // Get the current audio device label
    getAudioDeviceLabel() {
        return this.audioDeviceLabel;
    }
    
    // Enable or disable debug logging
    setDebug(enabled) {
        this.debug = enabled;
        if (enabled) {
            console.log('Audio detection debug logging enabled');
        }
    }
    
    // Enable or disable fifth validation strategy
    setFifthValidation(enabled) {
        this.enableFifthValidation = enabled;
        if (this.debug) {
            console.log(`Fifth validation ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    
    // Get current fifth validation state
    getFifthValidation() {
        return this.enableFifthValidation;
    }
    
    // Get current detection mode
    getDetectionMode() {
        return this.detectionMode;
    }
    
    // Enable or disable pre-filtering (band-pass)
    setPreFiltering(enabled) {
        this.enablePreFiltering = enabled;
        if (this.debug) {
            console.log(`Pre-filtering ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    
    // Get current pre-filtering state
    getPreFiltering() {
        return this.enablePreFiltering;
    }
    
    // Enable or disable HPS (Harmonic Product Spectrum)
    setHPS(enabled) {
        this.enableHPS = enabled;
        if (this.debug) {
            console.log(`HPS ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    
    // Get current HPS state
    getHPS() {
        return this.enableHPS;
    }
    
    // Enable or disable subharmonic check
    setSubharmonicCheck(enabled) {
        this.enableSubharmonicCheck = enabled;
        if (this.debug) {
            console.log(`Subharmonic check ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    
    // Get current subharmonic check state
    getSubharmonicCheck() {
        return this.enableSubharmonicCheck;
    }
    
    // Enable or disable adaptive threshold
    setAdaptiveThreshold(enabled) {
        this.enableAdaptiveThreshold = enabled;
        if (this.debug) {
            console.log(`Adaptive threshold ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    
    // Get current adaptive threshold state
    getAdaptiveThreshold() {
        return this.enableAdaptiveThreshold;
    }
    
    // Enable or disable strict harmonic checking
    setStrictHarmonics(enabled) {
        this.enableStrictHarmonics = enabled;
        if (this.debug) {
            console.log(`Strict harmonic checking ${enabled ? 'enabled' : 'disabled'}`);
        }
    }
    
    // Get current strict harmonic checking state
    getStrictHarmonics() {
        return this.enableStrictHarmonics;
    }
    
    // Set the number of required harmonics beyond the fundamental (1 to 10 inclusive)
    // Count 1 = root + 2x (2 total harmonics)
    // Count 2 = root + 2x + 3x (3 total harmonics)
    // Count 3 = root + 2x + 3x + 4x (4 total harmonics)
    // Count 4 = root + 2x + 3x + 4x + 5x (5 total harmonics)
    // Count 5 = root + 2x + 3x + 4x + 5x + 6x (6 total harmonics)
    // Count 6 = root + 2x + 3x + 4x + 5x + 6x + 7x (7 total harmonics)
    // Count 7 = root + 2x + 3x + 4x + 5x + 6x + 7x + 8x (8 total harmonics)
    // Count 8 = root + 2x + 3x + 4x + 5x + 6x + 7x + 8x + 9x (9 total harmonics)
    // Count 9 = root + 2x + 3x + 4x + 5x + 6x + 7x + 8x + 9x + 10x (10 total harmonics)
    // Count 10 = root + 2x + 3x + 4x + 5x + 6x + 7x + 8x + 9x + 10x + 11x (11 total harmonics)
    setRequiredHarmonicsCount(count) {
        if (typeof count !== 'number' || count < 1 || count > 10) {
            console.warn(`Invalid requiredHarmonicsCount: ${count}. Must be a number between 1 and 10.`);
            return;
        }
        
        this.requiredHarmonicsCount = count;
        if (this.debug) {
            console.log(`Required harmonics count set to: ${count}`);
        }
    }
    
    // Get current required harmonics count
    getRequiredHarmonicsCount() {
        return this.requiredHarmonicsCount;
    }
}

export default AudioDetection;
